// The sales pipeline's rules, read from rows (docs/pipeline-structure.md, section 9; Spencer, 2026-10-19).
//
// A stage, a seat and a step are each a row with a key set once; nothing here reads a name or an order to decide
// what a row means. A step applies to a lead when its applies-when condition (a key of the pipeline_conditions
// setting) holds over the lead's answers: first visit, design lane and the flags. Done advances the lead to the
// next step that applies; the stage moves when its last applying step is done, or when a gate step is done.

export type StageKind = 'working' | 'won' | 'parked' | 'lost';

export interface PipelineStage {
  id: string;
  key: string;
  name: string;
  sort: number;
  kind: StageKind;
  default_likelihood_pct: number | string | null;
  /** The default by design lane at this stage ({scope_only: 45, design_rounds: 90}); missing: default_likelihood_pct for every lane (decision 3, 2026-10-19). */
  likelihood_by_lane?: Record<string, number | string> | null;
}

export type DoneRule = 'seat_ticks' | 'owner_approves' | 'event' | 'client_acts';

export interface PipelineStep {
  id: string;
  key: string;
  stage_id: string;
  sort: number;
  name: string;
  seat_id: string;
  /** Worked out by the database from seat and done-when (ug.step_level), never typed; blank where the client acts (2026-10-22). */
  level: 'L0' | 'L1' | 'L2' | 'L3' | null;
  applies_when: string | null;
  done_rule: DoneRule;
  event_key: string | null;
  review_link: boolean;
  gate: boolean;
  back_to_step_id: string | null;
  retired_at: string | null;
  /** The answer this step records when done: first_visit, design_lane, or a flag key (Part 2). */
  sets_answer?: string | null;
  /** Done-when in words, shown under the step; and the note behind it. */
  done_when?: string | null;
  note?: string | null;
  /** How the step is done (read with the queue), when it last changed, and the dated lines its seat has learned doing it (2026-10-22). */
  instructions?: string | null;
  instructions_changed_at?: string | null;
  learned?: { id: string; on: string; text: string; by: string | null }[];
  /** The main tick button's own words ("Shared"); blank: Done. Data on the step (2026-10-22). */
  tick_words?: string | null;
  /** A second one-tap answer on the step ("Range already agreed"): ticks it and writes these words to the log as the reason. No skip (Spencer, 2026-10-22). */
  second_answer?: string | null;
  /** The step a lead parked on this one returns to once this one is retired (asked for at Retire; 2026-10-22). */
  replaced_by_step_id?: string | null;
}

/** One row of the pipeline_conditions setting: a step applies when every answer in `when` matches the lead's. */
export interface PipelineCondition {
  key: string;
  label: string;
  when: Record<string, string | boolean>;
  /** The words a step skipped under this condition shows on the lead's step list ("returning client"); blank: a skipped step is not listed (2026-10-22). */
  skip_words?: string | null;
}

/**
 * The facts a condition may read that are worked out, never stored (2026-10-22): a new lead (its primary contact on no
 * earlier opportunity, never parked) and a date that has passed. `reply_by_passed` is a date fact: a step on it is
 * re-evaluated while the lead sits in the stage (stepOnNow), never read once as the lead passes.
 */
export const FACT_KEYS = ['new_lead', 'reply_by_passed'] as const;
export const DATE_FACT_KEYS = ['reply_by_passed'] as const;

/** What a lead answers to: the two named fields, the flags, and the worked-out facts, as an opportunity row carries them. */
export interface LeadAnswers {
  first_visit?: string | null;
  design_lane?: string | null;
  pipeline_flags?: Record<string, unknown> | null;
  /** Worked out by leadFacts; null when not known. */
  new_lead?: boolean | null;
  reply_by_passed?: boolean | null;
}

/**
 * The worked-out facts about a lead (2026-10-22). A lead is new when its primary contact is on no earlier opportunity
 * (any outcome, open and parked included, not counting the one this lead was split from) and the lead has never been
 * parked. Reply-by has passed when the date is before today. Read when needed, never stored.
 */
export function leadFacts(input: { earlierOpportunities: number; everParked: boolean; replyBy: string | null | undefined; today: string }): Pick<LeadAnswers, 'new_lead' | 'reply_by_passed'> {
  const reply = input.replyBy ? String(input.replyBy).slice(0, 10) : null;
  return {
    new_lead: input.earlierOpportunities === 0 && !input.everParked,
    reply_by_passed: reply ? reply < input.today : false,
  };
}

/** A condition that reads a date fact: a step on it lands the day the date passes, not only as the lead passes it. */
export function isDateCondition(cond: PipelineCondition | null | undefined): boolean {
  return !!cond && Object.keys(cond.when).some((k) => (DATE_FACT_KEYS as readonly string[]).includes(k));
}

export interface Lead extends LeadAnswers {
  pipeline_stage_id?: string | null;
  current_step_id?: string | null;
  outcome?: string | null;
  likelihood_pct?: number | string | null;
}

export type Applies = 'yes' | 'no' | 'unknown';

const answer = (lead: LeadAnswers, attribute: string): unknown => {
  if (attribute === 'first_visit') return lead.first_visit ?? null;
  if (attribute === 'design_lane') return lead.design_lane ?? null;
  if (attribute === 'new_lead') return lead.new_lead ?? null;
  if (attribute === 'reply_by_passed') return lead.reply_by_passed ?? null;
  const v = lead.pipeline_flags?.[attribute];
  return v === undefined ? null : v;
};

/**
 * Whether a step applies to a lead (named apart from the job workflow's stepApplies, which the package also exports). A step with no condition applies to every lead. A condition the setting does
 * not know is treated as unanswered, never as a silent no. 'unknown' means the lead has not answered yet (first
 * visit not decided, design lane not set): the step is shown and counted, and Spencer decides at placement.
 */
export function stepAppliesToLead(step: Pick<PipelineStep, 'applies_when'>, conditions: PipelineCondition[], lead: LeadAnswers): Applies {
  if (!step.applies_when) return 'yes';
  const cond = conditions.find((c) => c.key === step.applies_when);
  if (!cond) return 'unknown';
  let unknown = false;
  for (const [attribute, wanted] of Object.entries(cond.when)) {
    const have = answer(lead, attribute);
    if (have === null) {
      unknown = true;
      continue;
    }
    if (have !== wanted) return 'no';
  }
  return unknown ? 'unknown' : 'yes';
}

/** The stages a lead moves through, in order (the side rows left out). */
export function workingStages(stages: PipelineStage[]): PipelineStage[] {
  return stages.filter((s) => s.kind === 'working').sort((a, b) => a.sort - b.sort);
}

/** A stage's live steps in order. */
export function stepsOfStage(steps: PipelineStep[], stageId: string): PipelineStep[] {
  return steps.filter((s) => s.stage_id === stageId && !s.retired_at).sort((a, b) => a.sort - b.sort);
}

/** The steps of a stage that apply to the lead (an unanswered condition counts as applying). */
export function applyingSteps(steps: PipelineStep[], stageId: string, conditions: PipelineCondition[], lead: LeadAnswers): PipelineStep[] {
  return stepsOfStage(steps, stageId).filter((s) => stepAppliesToLead(s, conditions, lead) !== 'no');
}

/**
 * The step a lead is on now, read live (2026-10-22): its stored step, unless an earlier step of the stage on a date
 * condition has come true and is not done yet, which then lands ("Decide-by date: call it" the day reply-by passes with
 * the deposit gate still open). A date step the lead passed before its date is not skipped for good.
 */
export function stepOnNow(steps: PipelineStep[], conditions: PipelineCondition[], lead: LeadAnswers & { outcome?: string | null }, doneStepIds: ReadonlySet<string>, currentStepId: string | null | undefined): PipelineStep | null {
  const current = steps.find((s) => s.id === currentStepId) ?? null;
  if (!current) return null;
  if (lead.outcome && lead.outcome !== 'open') return current;
  const landed = stepsOfStage(steps, current.stage_id).find((s) => {
    if (s.sort >= current.sort || doneStepIds.has(s.id)) return false;
    const cond = conditions.find((c) => c.key === s.applies_when);
    return isDateCondition(cond) && stepAppliesToLead(s, conditions, lead) === 'yes';
  });
  return landed ?? current;
}

/** `pending` (2026-10-24, the step bar): a step whose condition is not settled yet, a date not yet passed or an answer not yet given; it fills in when it applies. */
export type StepListStatus = 'done' | 'skipped' | 'current' | 'todo' | 'pending';

export interface StepListRow {
  step: PipelineStep;
  status: StepListStatus;
  /** On a skipped row: why, from the condition's skip_words ("returning client"). */
  words?: string;
  /** On a skipped or pending row: the condition it was skipped under, or that is not settled yet. */
  condition?: PipelineCondition;
}

/**
 * The lead's step list for its stage (Iris's panel 2, 2026-10-22): every live step that applies, done, on now or still to
 * do, plus a step that does not apply when its condition carries skip_words, shown as skipped with those words. The rest
 * of the non-applying steps are left out: the mark shows only where it acts.
 */
export function stepListFor(steps: PipelineStep[], stageId: string, conditions: PipelineCondition[], lead: LeadAnswers, doneStepIds: ReadonlySet<string>, skippedStepIds: ReadonlySet<string>, currentStepId: string | null | undefined): StepListRow[] {
  const out: StepListRow[] = [];
  const live = stepsOfStage(steps, stageId);
  const at = live.findIndex((s) => s.id === currentStepId);
  for (const [i, s] of live.entries()) {
    if (s.id === currentStepId) {
      out.push({ step: s, status: 'current' });
      continue;
    }
    const applies = stepAppliesToLead(s, conditions, lead);
    if (skippedStepIds.has(s.id)) {
      out.push({ step: s, status: 'skipped' });
      continue;
    }
    const cond = conditions.find((c) => c.key === s.applies_when);
    // not settled yet (the step bar, 2026-10-24): a date condition still to pass, or an answer not yet given, ahead of the step on now
    if ((applies === 'no' && isDateCondition(cond) && (at < 0 || i > at)) || (applies === 'unknown' && cond && (at < 0 || i > at))) {
      out.push({ step: s, status: 'pending', condition: cond });
      continue;
    }
    if (applies === 'no') {
      if (cond?.skip_words) out.push({ step: s, status: 'skipped', words: cond.skip_words, condition: cond });
      continue;
    }
    // done, or passed: a step before the one on now that applies reads as done, as the strip reads it (a date step left behind is the one exception: it comes back when its date passes)
    const passed = at >= 0 && i < at && !isDateCondition(conditions.find((c) => c.key === s.applies_when));
    out.push({ step: s, status: doneStepIds.has(s.id) || passed ? 'done' : 'todo' });
  }
  return out;
}

/** A retired step's replacement, followed to a live step (a lead parked on a retired step returns there); the step itself when live; null when nothing live is left. */
export function returnStep(stepId: string | null | undefined, steps: PipelineStep[]): PipelineStep | null {
  let step = steps.find((s) => s.id === stepId) ?? null;
  for (let i = 0; step && step.retired_at && i < 20; i++) step = steps.find((s) => s.id === step!.replaced_by_step_id) ?? null;
  return step && !step.retired_at ? step : null;
}

/** "Returns to Intake · Capture the lead" for a parked lead: the stage and step it keeps (2026-10-22); null when no step is on record (parked at import). */
export function parkedReturn(lead: { pipeline_stage_id?: string | null; current_step_id?: string | null }, stages: PipelineStage[], steps: PipelineStep[]): { stage: PipelineStage; step: PipelineStep } | null {
  const step = returnStep(lead.current_step_id, steps);
  const stage = stages.find((s) => s.id === (step?.stage_id ?? lead.pipeline_stage_id));
  return step && stage ? { stage, step } : null;
}

/**
 * The rules an outcome must meet (Spencer, 2026-10-22), the same in the app, the connector and the database: a park needs
 * a next touch (the re-approach date); a loss needs a reason; a won lead takes no next touch. Null when the outcome may go.
 */
export function outcomeRefusal(outcome: string, given: { nextTouch?: string | null; reason?: string | null }): string | null {
  if (outcome === 'parked' && !given.nextTouch) return 'A park needs a next touch: the date to re-approach.';
  if (outcome === 'lost' && !(given.reason ?? '').trim()) return 'A loss needs a reason.';
  if (outcome === 'won' && given.nextTouch) return 'A won lead takes no next touch.';
  return null;
}

/** The next touch a loss sets: today plus the setting's weeks (10 by default), YYYY-MM-DD; null when the setting says none. */
export function lostNextTouch(today: string, weeks: number | string | null | undefined): string | null {
  const w = Number(weeks);
  if (!Number.isFinite(w) || w <= 0) return null;
  const d = new Date(`${today.slice(0, 10)}T12:00:00`);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + Math.round(w * 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A contact's history across the other opportunities they are on: how many earlier ones, won and lost, for the returning-client line (2026-10-22). */
export function contactHistory(
  opps: { id: string; primary_contact_id?: string | null; outcome?: string | null; created_at?: string | null; retired_at?: string | null }[],
  links: { opportunity_id: string; contact_id: string }[],
  contactId: string | null | undefined,
  lead: { id: string; created_at?: string | null; split_from_id?: string | null },
): { earlier: number; won: number; lost: number } {
  if (!contactId) return { earlier: 0, won: 0, lost: 0 };
  const on = opps.filter((o) => o.id !== lead.id && !o.retired_at && o.id !== lead.split_from_id && (o.primary_contact_id === contactId || links.some((l) => l.opportunity_id === o.id && l.contact_id === contactId)));
  const earlier = on.filter((o) => !lead.created_at || !o.created_at || String(o.created_at) < String(lead.created_at));
  return { earlier: earlier.length, won: on.filter((o) => o.outcome === 'won').length, lost: on.filter((o) => o.outcome === 'lost').length };
}

/** A lead's log entry (ug.activity) as the two rules below read it: a tap's entry carries what it was in `payload.prev`. */
export interface TapEntry {
  id: string;
  kind: string;
  payload?: { action?: string; prev?: { pipeline_stage_id?: string | null; current_step_id?: string | null } | null } | null;
}

/**
 * The tap the log can still take back: the newest step or stage entry that carries what the lead was before it. Taps
 * come off newest first, one at a time, so only this one offers Undo. `log` is newest first.
 */
export function undoableEntry<T extends TapEntry>(log: T[]): T | null {
  return log.find((a) => (a.kind === 'step' || a.kind === 'stage') && !!a.payload?.prev) ?? null;
}

/**
 * Where a move by hand on the stage track lands (Spencer, 2026-10-22): back to a stage the lead has stood on, the step it
 * was on when it left (the newest entry made while it stood there), when that step still applies; otherwise, and on a
 * stage it has never been in, the stage's first applying step. `log` is newest first.
 */
export function landingStep(steps: PipelineStep[], stageId: string, conditions: PipelineCondition[], lead: LeadAnswers, log: TapEntry[]): PipelineStep | null {
  const applying = applyingSteps(steps, stageId, conditions, lead);
  const left = log.find((a) => a.payload?.prev?.pipeline_stage_id === stageId && a.payload.prev.current_step_id);
  return applying.find((s) => s.id === left?.payload?.prev?.current_step_id) ?? applying[0] ?? null;
}

export interface NextStep {
  step: PipelineStep;
  stage: PipelineStage;
  /** True when the lead left its stage to get here. */
  stageMoved: boolean;
}

/**
 * Where a lead goes when `from` is done (or where it starts, when `from` is null: the first applying step of its
 * stage). Done advances to the next applying step of the same stage; the stage moves when none is left or when
 * `from` is a gate. After the last working stage there is nowhere to go: null, and the caller marks the outcome.
 */
export function nextStep(
  steps: PipelineStep[],
  stages: PipelineStage[],
  conditions: PipelineCondition[],
  lead: Lead,
  from: PipelineStep | null,
): NextStep | null {
  const working = workingStages(stages);
  const stageId = from?.stage_id ?? lead.pipeline_stage_id ?? working[0]?.id;
  let at = working.findIndex((s) => s.id === stageId);
  if (at < 0) return null;
  if (from && !from.gate) {
    const later = applyingSteps(steps, from.stage_id, conditions, lead).filter((s) => s.sort > from.sort);
    if (later[0]) return { step: later[0], stage: working[at]!, stageMoved: false };
  } else if (!from) {
    const first = applyingSteps(steps, working[at]!.id, conditions, lead)[0];
    if (first) return { step: first, stage: working[at]!, stageMoved: false };
  }
  for (at = at + 1; at < working.length; at++) {
    const first = applyingSteps(steps, working[at]!.id, conditions, lead)[0];
    if (first) return { step: first, stage: working[at]!, stageMoved: true };
  }
  return null;
}

const pct = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * How sure we are a lead lands: its own figure when it has one, else its stage's default for its design lane (Design &
 * Price: 45 scope only, 90 design rounds; the stage's one figure where no lane figure is set or the lane is not decided);
 * a parked or lost lead reads the Parked or Lost row; halved when the lead has gone quiet (rounded to a whole percent,
 * as the board does).
 */
export function likelihoodFor(lead: Lead, stages: PipelineStage[], quiet = false): number | null {
  let base = pct(lead.likelihood_pct);
  if (base === null) {
    const side = lead.outcome === 'parked' ? 'parked' : lead.outcome === 'lost' ? 'lost' : lead.outcome === 'won' ? 'won' : null;
    const row = side ? stages.find((s) => s.kind === side) : stages.find((s) => s.id === lead.pipeline_stage_id);
    const byLane = !side && lead.design_lane && row?.likelihood_by_lane ? pct(row.likelihood_by_lane[lead.design_lane]) : null;
    base = byLane ?? pct(row?.default_likelihood_pct);
  }
  if (base === null) return null;
  return quiet ? Math.round(base / 2) : base;
}

/** The events in ZeroGap the database can see for itself, as facts about one lead. The rest (an invoice raised or paid, a deposit recorded) stay unseen until QuickBooks is read. */
export interface EventFacts {
  /** A contact is linked to the lead. */
  hasContact: boolean;
  /** A job of the lead has its Proposal workflow step done. */
  proposalDone: boolean;
  /** The client's Concept Board Send has landed for the current round. */
  conceptBoardSent?: boolean;
  /** The UG CRM script logged the intake text it sent (a touch of kind intake_text); no other outbound text counts (2026-10-22). */
  intakeTextLogged?: boolean;
  /** Every live job of the lead has its Engineering check workflow step done (docs/job-view-brief.md, section 1). */
  engineeringCheckDone?: boolean;
  /** The lead's next touch date has arrived (docs/step-changes-brief.md, 2026-10-24): the one date event, read while the lead sits on the step. */
  nextTouchArrived?: boolean;
}

/**
 * Whether an event step's event is already true for the lead (decision 4, 2026-10-19: an event step whose event is
 * already true ticks when a lead lands on it, placement included). An event the database cannot see reads false.
 */
export function eventIsTrue(eventKey: string | null | undefined, facts: EventFacts): boolean {
  switch (eventKey) {
    case 'lead_captured':
      return facts.hasContact;
    case 'job_proposal_done':
      return facts.proposalDone;
    case 'concept_board_send':
      return !!facts.conceptBoardSent;
    case 'intake_text_logged':
      return !!facts.intakeTextLogged;
    case 'job_engineering_check_done':
      return !!facts.engineeringCheckDone;
    case 'next_touch_arrived':
      return !!facts.nextTouchArrived;
    default:
      return false;
  }
}

/** Whether the lead's next touch date has arrived (the one date event, 2026-10-24): a bare date from the start of that day in the shop's own clock; a timestamp at or before now. */
export function nextTouchArrived(nextTouchAt: string | null | undefined, now: Date | number = Date.now()): boolean {
  if (!nextTouchAt) return false;
  const bare = /^\d{4}-\d{2}-\d{2}$/.test(nextTouchAt);
  const at = bare ? new Date(Number(nextTouchAt.slice(0, 4)), Number(nextTouchAt.slice(5, 7)) - 1, Number(nextTouchAt.slice(8, 10))).getTime() : Date.parse(nextTouchAt);
  if (Number.isNaN(at)) return false;
  return at <= (typeof now === 'number' ? now : now.getTime());
}

/** A retired record counts nowhere (decision 1, 2026-10-19): not in a lead's value, the tiles, or Won and lost. */
export function isRetired(row: { retired_at?: string | null } | null | undefined): boolean {
  return !!row?.retired_at;
}

/** A dollar figure typed as a number (Notion's Est. Value) to whole cents, exactly. */
export function dollarsToCents(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const s = String(v).trim().replace(/[$,]/g, '');
  if (!/^-?\d*\.?\d+$/.test(s)) return null;
  const [whole, frac = ''] = s.replace('-', '').split('.');
  const cents = Number(whole || '0') * 100 + Number((frac + '00').slice(0, 2)) + (Number(frac.charAt(2) || '0') >= 5 ? 1 : 0);
  return s.startsWith('-') ? -cents : cents;
}

// ---------- what the screens read (Part 2, the screens; Spencer, 2026-10-19) ----------

/** A lead's step row: ready (the seat's beat of "Owner approves"), done, or skipped; which round of a loop; the review link left behind. */
export interface OpportunityStepRow {
  id: string;
  opportunity_id: string;
  step_id: string;
  round: number;
  status: 'ready' | 'done' | 'skipped';
  at: string;
  by_user_id?: string | null;
  for_user_id?: string | null;
  review_link?: string | null;
  note?: string | null;
}

export type ValueKind = 'priced' | 'typed' | 'range' | 'none';

export interface LeadValue {
  kind: ValueKind;
  /** What the lead is worth, in cents: the priced jobs' sum, else the typed estimate, else the middle of the range; null when nothing is known. */
  cents: number | null;
  priced_cents: number;
  typed_cents: number | null;
  low_cents: number | null;
  high_cents: number | null;
  /** Unverified items across the priced jobs. */
  unverified: number;
  jobs_priced: number;
}

/**
 * Value adds up job by job (sheet, section 8): a priced job shows its estimate price; an opportunity with no priced job
 * carries its typed estimate; a range once the Estimator has one. Cents throughout, never a float.
 */
export function leadValue(
  lead: { typed_value_cents?: number | string | null; range_low_cents?: number | string | null; range_high_cents?: number | string | null },
  jobs: { price_cents: number | null; unverified?: number }[],
): LeadValue {
  const int = (v: number | string | null | undefined): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : null;
  };
  const priced = jobs.filter((j) => j.price_cents !== null && j.price_cents !== undefined);
  const priced_cents = priced.reduce((n, j) => n + (j.price_cents ?? 0), 0);
  const unverified = priced.reduce((n, j) => n + (j.unverified ?? 0), 0);
  const typed_cents = int(lead.typed_value_cents);
  const low_cents = int(lead.range_low_cents);
  const high_cents = int(lead.range_high_cents);
  if (priced.length > 0) return { kind: 'priced', cents: priced_cents, priced_cents, typed_cents, low_cents, high_cents, unverified, jobs_priced: priced.length };
  if (typed_cents !== null) return { kind: 'typed', cents: typed_cents, priced_cents: 0, typed_cents, low_cents, high_cents, unverified: 0, jobs_priced: 0 };
  if (low_cents !== null && high_cents !== null) return { kind: 'range', cents: Math.round((low_cents + high_cents) / 2), priced_cents: 0, typed_cents, low_cents, high_cents, unverified: 0, jobs_priced: 0 };
  return { kind: 'none', cents: null, priced_cents: 0, typed_cents, low_cents, high_cents, unverified: 0, jobs_priced: 0 };
}

const DAY = 86_400_000;

/** Whole days from a timestamp or date to `now`; null when there is none. A date reads as noon so every time zone agrees. */
export function daysBetween(from: string | Date | null | undefined, now: Date | number = Date.now()): number | null {
  if (!from) return null;
  const s = from instanceof Date ? from.toISOString() : String(from);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T12:00:00`) : new Date(s);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Number(now) - d.getTime()) / DAY);
}

/** Days since the newest touch; null when the lead was never touched. */
export const quietDays = daysBetween;

export interface QuietSetting {
  /** Quiet after this many days with no touch. */
  days: number;
  /** A stage's own figure, by stage key (the Landing Board's nudge days carried over: Intake 3, Scope 5); a stage not named uses `days`. */
  by_stage?: Record<string, number> | null;
}

/** The quiet threshold in days for a stage: its own figure when the setting names it, else the one figure. */
export function quietDaysFor(setting: QuietSetting | null | undefined, stageKey: string | null | undefined): number | null {
  const own = stageKey && setting?.by_stage ? Number(setting.by_stage[stageKey]) : NaN;
  const limit = Number.isFinite(own) && own > 0 ? own : Number(setting?.days);
  return Number.isFinite(limit) && limit > 0 ? limit : null;
}

/** Gone quiet: at least the stage's days since the last touch (a lead never touched is quiet too). The likelihood halves (likelihoodFor). */
export function isQuiet(days: number | null, setting: QuietSetting | null | undefined, stageKey?: string | null): boolean {
  const limit = quietDaysFor(setting, stageKey);
  if (limit === null) return false;
  return days === null || days >= limit;
}

/** How late a date is, in whole days; 0 when it is today or still ahead; null when there is no date. */
export function daysLate(date: string | null | undefined, now: Date | number = Date.now()): number | null {
  const d = daysBetween(date ? String(date).slice(0, 10) : null, now);
  return d === null ? null : Math.max(0, d);
}

export type StripSegment = 'done' | 'current' | 'todo' | 'off';

export interface StepPosition {
  /** The current step's place among the stage's live steps, 1-based; 0 when the lead has no step yet. */
  index: number;
  /** Every live step of the stage, applying or not: the strip's segments. */
  total: number;
  /** One segment per live step: done, current, still to do, or off (does not apply to this lead, drawn dotted). */
  strip: StripSegment[];
}

/** "n of m" and the step strip on a card: the stage's live steps, the ones done or passed filled, the current one lit, the ones that do not apply dotted. */
export function stepPosition(steps: PipelineStep[], stageId: string, conditions: PipelineCondition[], lead: LeadAnswers, doneStepIds: ReadonlySet<string>, currentStepId: string | null | undefined): StepPosition {
  const live = stepsOfStage(steps, stageId);
  const at = live.findIndex((s) => s.id === currentStepId);
  const strip: StripSegment[] = live.map((s, i) => {
    if (s.id === currentStepId) return 'current';
    if (stepAppliesToLead(s, conditions, lead) === 'no') return 'off';
    if (doneStepIds.has(s.id) || (at >= 0 && i < at)) return 'done';
    return 'todo';
  });
  return { index: at + 1, total: live.length, strip };
}

/** The step on now is waiting on the Owner seat: it is the Owner's own, or it is "Owner approves" and the seat has marked it ready. */
export function needsOwner(step: Pick<PipelineStep, 'seat_id' | 'done_rule'> | null | undefined, ownerSeatId: string | null | undefined, readyRow: OpportunityStepRow | null | undefined): boolean {
  if (!step) return false;
  if (ownerSeatId && step.seat_id === ownerSeatId) return true;
  return step.done_rule === 'owner_approves' && readyRow?.status === 'ready';
}

export type NeedsGroup = 'calls' | 'reply' | 'quiet';

export interface NeedsInput {
  needsOwner: boolean;
  /** Reply-by passed, next touch passed, or a parked lead's re-approach date passed. */
  late: boolean;
  quiet: boolean;
  valueCents: number | null;
  likelihoodPct: number | null;
}

export interface NeedsRow<T extends NeedsInput> {
  lead: T;
  group: NeedsGroup;
  /** 1..n over the whole list; shown only on the phone's Needs you list (Spencer, 2026-10-19). */
  rank: number;
  /** Value × likelihood, in cents: what the row is sorted by. */
  score: number;
}

/**
 * Needs you ranks the Owner's steps first, then dates that passed, then leads gone quiet, last (sheet, section 8). Within
 * a group by value × likelihood; the quiet group by value alone. A lead that is both the Owner's and quiet is a call.
 */
export function rankNeedsYou<T extends NeedsInput>(leads: T[]): NeedsRow<T>[] {
  const score = (l: NeedsInput) => Math.round((l.valueCents ?? 0) * ((l.likelihoodPct ?? 0) / 100));
  const groupOf = (l: NeedsInput): NeedsGroup | null => (l.needsOwner ? 'calls' : l.late ? 'reply' : l.quiet ? 'quiet' : null);
  const order: NeedsGroup[] = ['calls', 'reply', 'quiet'];
  const rows = leads
    .map((lead) => ({ lead, group: groupOf(lead), score: score(lead) }))
    .filter((r): r is { lead: T; group: NeedsGroup; score: number } => r.group !== null)
    .sort((a, b) => {
      const g = order.indexOf(a.group) - order.indexOf(b.group);
      if (g !== 0) return g;
      const sa = a.group === 'quiet' ? a.lead.valueCents ?? 0 : a.score;
      const sb = b.group === 'quiet' ? b.lead.valueCents ?? 0 : b.score;
      return sb - sa;
    });
  return rows.map((r, i) => ({ ...r, rank: i + 1 }));
}

export interface ReviewLink {
  link: string;
  step: PipelineStep;
  round: number;
}

/**
 * The thing to review beside the step on now: the current step's own ready row's link (Owner approves), else the newest
 * done step's link on this lead where that step leaves one. Null when there is nothing to open.
 */
export function reviewLinkFor(rows: OpportunityStepRow[], steps: PipelineStep[], current: PipelineStep | null | undefined): ReviewLink | null {
  const byId = new Map(steps.map((s) => [s.id, s]));
  if (current) {
    const ready = rows.find((r) => r.step_id === current.id && r.status === 'ready' && r.review_link);
    if (ready) return { link: ready.review_link!, step: current, round: ready.round };
  }
  const done = rows
    .filter((r) => r.status === 'done' && r.review_link && byId.get(r.step_id)?.review_link)
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const d = done[0];
  return d ? { link: d.review_link!, step: byId.get(d.step_id)!, round: d.round } : null;
}

/**
 * Lost before or after a proposal (sheet, section 8: "read from whether the proposal step was done"). Since 2026-10-22 the
 * proposal is written by the Pipeline manager and leaves no event, so the rule reads the process: the proposal has gone
 * out once the lead has a done row on any step of the last working stage (Decision opens with Deliver the Proposal) or of
 * Won. An imported loss carries no steps, so it reads before.
 */
export function afterProposal(rows: OpportunityStepRow[], steps: PipelineStep[], stages: PipelineStage[]): boolean {
  const working = workingStages(stages);
  const last = working[working.length - 1];
  if (!last) return false;
  const stageSort = new Map(stages.map((s) => [s.id, s.sort]));
  const byId = new Map(steps.map((s) => [s.id, s]));
  return rows.some((r) => {
    const s = byId.get(r.step_id);
    return r.status === 'done' && !!s && (stageSort.get(s.stage_id) ?? -1) >= last.sort;
  });
}

export interface ClosedLead {
  outcome: 'won' | 'lost';
  /** The row it falls under: its source, its contact's type, its size band, its year. */
  group: string;
  cents: number;
  afterProposal: boolean;
}

export interface WinRow {
  group: string;
  won: number;
  lostBefore: number;
  lostAfter: number;
  wonCents: number;
  /** Won over won plus lost, a whole percent; null when nothing closed. */
  ratePct: number | null;
}

/** Won and lost by a grouping, biggest row first, with the totals row last (group ''). */
export function winRows(closed: ClosedLead[]): WinRow[] {
  const by = new Map<string, WinRow>();
  const row = (g: string) => {
    let r = by.get(g);
    if (!r) {
      r = { group: g, won: 0, lostBefore: 0, lostAfter: 0, wonCents: 0, ratePct: null };
      by.set(g, r);
    }
    return r;
  };
  for (const c of closed) {
    for (const r of [row(c.group), row('')]) {
      if (c.outcome === 'won') {
        r.won++;
        r.wonCents += c.cents;
      } else if (c.afterProposal) r.lostAfter++;
      else r.lostBefore++;
    }
  }
  const rows = [...by.values()].map((r) => {
    const n = r.won + r.lostBefore + r.lostAfter;
    return { ...r, ratePct: n === 0 ? null : Math.round((r.won / n) * 100) };
  });
  const total = rows.find((r) => r.group === '');
  const size = (r: WinRow) => r.won + r.lostBefore + r.lostAfter;
  const rest = rows.filter((r) => r.group !== '').sort((a, b) => size(b) - size(a) || a.group.localeCompare(b.group));
  return total ? [...rest, total] : rest;
}

/** The job-size band a value falls in, from the dollar lines of the job_size_bands setting: "Under $5,000", "$5,000 to $20,000", "Over $20,000"; "No value" with none. */
export function sizeBand(cents: number | null | undefined, bandsDollars: number[]): string {
  if (cents === null || cents === undefined) return 'No value';
  const lines = [...bandsDollars].map(Number).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  const dollars = (n: number) => `$${n.toLocaleString('en-US')}`;
  if (lines.length === 0) return 'All sizes';
  for (const [i, line] of lines.entries()) {
    if (cents < line * 100) return i === 0 ? `Under ${dollars(line)}` : `${dollars(lines[i - 1]!)} to ${dollars(line)}`;
  }
  return `Over ${dollars(lines[lines.length - 1]!)}`;
}

/** Next touch starts the day after reply-by (sheet, section 1). YYYY-MM-DD in, the same out. */
export function dayAfter(date: string | null | undefined): string | null {
  if (!date) return null;
  const d = new Date(`${String(date).slice(0, 10)}T12:00:00`);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A step key from its name, for a step added on the Pipeline steps tab: lower case, words joined by underscores, unique against the keys there. */
export function stepKeyFrom(name: string, taken: ReadonlySet<string>): string {
  const base = name.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'step';
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`;
  return key;
}
